/**
 * API 客戶端
 * 提供與後端 API 通信的基本功能
 */
import { ApiRequestOptions } from "./types";
import { defaultHeaders, withAuth } from "./interceptors/authInterceptor";
import { handleApiError } from "./interceptors/errorInterceptor";

// 從環境變數中獲取 API 基礎 URL
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

// 請求逾時時間 (毫秒)
const REQUEST_TIMEOUT = 5 * 60 * 1000;

/**
 * 建立完整的 URL，包含查詢參數
 * @param endpoint API 端點
 * @param params 查詢參數
 * @returns 完整的 URL
 */
function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  const url = new URL(`${API_BASE_URL}${endpoint}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * 發送 API 請求的基本函數
 * @param endpoint API 端點路徑
 * @param options 請求選項
 * @returns Promise 包含響應數據
 */
async function fetchApi<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { authenticated = true, params, ...fetchOptions } = options;

  // 構建 URL
  const url = buildUrl(endpoint, params);

  // 構建請求選項
  const requestOptions: RequestInit = {
    ...fetchOptions,
    headers: authenticated
      ? withAuth({ ...defaultHeaders, ...fetchOptions.headers })
      : { ...defaultHeaders, ...fetchOptions.headers },
  };

  // 使用 Promise.race 實現請求超時
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`請求超時: ${endpoint} (${REQUEST_TIMEOUT}ms)`));
    }, REQUEST_TIMEOUT);
  });

  try {
    // 發送請求並設置超時
    const response = (await Promise.race([
      fetch(url, requestOptions),
      timeoutPromise,
    ])) as Response;

    // 檢查響應狀態
    if (!response.ok) {
      return handleApiError(response);
    }

    // 如果響應是 204 No Content，返回 null
    if (response.status === 204) {
      return null as unknown as T;
    }

    // 解析 JSON 響應
    return await response.json();
  } catch (error) {
    // 處理超時或網絡錯誤
    const errorMessage = error instanceof Error ? error.message : "未知錯誤";
    console.error(`API 請求失敗: ${errorMessage}`);
    throw error;
  }
}

/**
 * API 客戶端物件，提供 HTTP 請求方法
 */
export const apiClient = {
  /**
   * u767cu9001 GET u8acbu6c42
   * @param endpoint API u7aefu9ede
   * @param options u8acbu6c42u9078u9805
   * @returns Promise u5305u542bu97ffu61c9u6578u64da
   */
  get: <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
    return fetchApi<T>(endpoint, {
      method: "GET",
      ...options,
    });
  },

  /**
   * u767cu9001 POST u8acbu6c42
   * @param endpoint API u7aefu9ede
   * @param data u8acbu6c42u6578u64da
   * @param options u8acbu6c42u9078u9805
   * @returns Promise u5305u542bu97ffu61c9u6578u64da
   */
  post: <T>(
    endpoint: string,
    data?: any,
    options?: ApiRequestOptions,
  ): Promise<T> => {
    return fetchApi<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  },

  /**
   * u767cu9001 PUT u8acbu6c42
   * @param endpoint API u7aefu9ede
   * @param data u8acbu6c42u6578u64da
   * @param options u8acbu6c42u9078u9805
   * @returns Promise u5305u542bu97ffu61c9u6578u64da
   */
  put: <T>(
    endpoint: string,
    data?: any,
    options?: ApiRequestOptions,
  ): Promise<T> => {
    return fetchApi<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  },

  /**
   * u767cu9001 PATCH u8acbu6c42
   * @param endpoint API u7aefu9ede
   * @param data u8acbu6c42u6578u64da
   * @param options u8acbu6c42u9078u9805
   * @returns Promise u5305u542bu97ffu61c9u6578u64da
   */
  patch: <T>(
    endpoint: string,
    data?: any,
    options?: ApiRequestOptions,
  ): Promise<T> => {
    return fetchApi<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  },

  /**
   * u767cu9001 DELETE u8acbu6c42
   * @param endpoint API u7aefu9ede
   * @param options u8acbu6c42u9078u9805
   * @returns Promise u5305u542bu97ffu61c9u6578u64da
   */
  delete: <T>(endpoint: string, options?: ApiRequestOptions): Promise<T> => {
    return fetchApi<T>(endpoint, {
      method: "DELETE",
      ...options,
    });
  },
};
